import { ActionType } from 'typesafe-actions';
import * as actions from './actions';

import {
    RecordingTypeKeys, PartyContextValues, RecorderStatus, ModuleStatus, RecordedAudioValues,
} from './types';

export type ExplanationRecording = ActionType<typeof actions>;

export interface RecordingState {
    readonly partyState?: PartyContextValues;
    readonly recorderStatus: RecorderStatus;
    readonly previousRecorderStatus: RecorderStatus;
    readonly moduleStatus: ModuleStatus;
    readonly recordedTime: number;
    readonly recorderIntervalId: number;
    readonly workspaceId: string;
    readonly questionId: number;
    readonly featureId: string;
    readonly recordedAudio: RecordedAudioValues;
    readonly assessmentRunId?: number;
}

export const initialState: RecordingState = {
    partyState: {
        partyName: '',
        partyId: -1,
    },
    recordedTime: 0,
    recorderStatus: RecorderStatus.UNINITIALIZED,
    previousRecorderStatus: RecorderStatus.UNINITIALIZED,
    moduleStatus: ModuleStatus.UNINITIALIZED,
    recorderIntervalId: -1,
    workspaceId: '',
    questionId: -1,
    featureId: '',
    recordedAudio: {
        filename: '',
        recordedBlob: null,
    },
};

export const explanationRecordingReducer = (state: RecordingState = initialState,
    action: ExplanationRecording): RecordingState => {
    switch (action.type) {
    case RecordingTypeKeys.ENACT_RECORDING:
        return {
            ...state,
            partyState: {
                ...state.partyState,
                ...action.payload.partyState,
            },
            moduleStatus: ModuleStatus.AWAITING_MIC_PERMISSION,
            workspaceId: action.payload.workspaceId,
            questionId: action.payload.questionId,
            featureId: action.payload.featureId,
            assessmentRunId: action.payload.assessmentRunId,
        };
    case RecordingTypeKeys.SET_RECORDER_INTERVAL_ID:
        return {
            ...state,
            recorderIntervalId: action.payload,
        };
    case RecordingTypeKeys.INCREMENT_RECORDED_TIME:
        const newRecordedTime = state.recordedTime + 1;
        return {
            ...state,
            recordedTime: newRecordedTime,
        };
    case RecordingTypeKeys.INITIATE_TRANSCODING_OF_RECORDED_AUDIO:
        return {
            ...state,
            previousRecorderStatus: state.recorderStatus,
            recorderStatus: action.payload ? RecorderStatus.SAVING_WAITING_FOR_WORKSPACE : RecorderStatus.SAVING,
            moduleStatus: ModuleStatus.STOPPED,
        };
    case RecordingTypeKeys.SAVE_RECORDED_AUDIO:
        return {
            ...state,
            previousRecorderStatus: state.recorderStatus,
            recorderStatus: RecorderStatus.SAVING,
        };
    case RecordingTypeKeys.SAVE_RECORDED_AUDIO_BUT_WAIT_FOR_WORKSPACE:
        return {
            ...state,
            previousRecorderStatus: state.recorderStatus,
            recorderStatus: RecorderStatus.SAVING_WAITING_FOR_WORKSPACE,
        };
    case RecordingTypeKeys.SAVE_RECORDED_AUDIO_BUT_WAIT_FOR_PARTY:
        return {
            ...state,
            previousRecorderStatus: state.recorderStatus,
            recorderStatus: RecorderStatus.SAVING_WAITING_FOR_PARTY,
        };
    case RecordingTypeKeys.SET_WORKSPACE_RESTORED:
        if (state.recorderStatus === RecorderStatus.AWAITING_FILENAME_AND_WORKSPACE) {
            return {
                ...state,
                recorderStatus: RecorderStatus.AWAITING_FILENAME,
                previousRecorderStatus: state.recorderStatus,
            };
        }
        return {
            ...state,
            previousRecorderStatus: state.recorderStatus,
            recorderStatus: RecorderStatus.SAVING,
        };
    case RecordingTypeKeys.SAVE_RECORDED_AUDIO_SUCCESS:
        return {
            ...initialState,
        };
    case RecordingTypeKeys.SAVE_RECORDED_AUDIO_FAILED:
        return {
            ...state,
            previousRecorderStatus: state.recorderStatus,
            recorderStatus: RecorderStatus.SAVING_FAILED,
        };
    case RecordingTypeKeys.REJECT_RECORDING_PERMISSION:
        return {
            ...state,
            moduleStatus: ModuleStatus.BLOCKED,
        };
    case RecordingTypeKeys.GRANT_RECORDING_PERMISSION:
        return {
            ...state,
            moduleStatus: ModuleStatus.RECORDING,
            recorderStatus: RecorderStatus.RECORDING,
            previousRecorderStatus: state.recorderStatus,
        };
    case RecordingTypeKeys.PAUSE_RECORDING:
        return {
            ...state,
            moduleStatus: ModuleStatus.PAUSED,
            recorderStatus: RecorderStatus.PAUSED,
            previousRecorderStatus: state.recorderStatus,
        };
    case RecordingTypeKeys.RESUME_RECORDING:
        return {
            ...state,
            moduleStatus: ModuleStatus.AWAITING_MIC_PERMISSION,
            recorderStatus: RecorderStatus.RECORDING,
            previousRecorderStatus: state.recorderStatus,
        };
    case RecordingTypeKeys.ASK_CANCEL_RECORDING:
        if (state.moduleStatus === ModuleStatus.RECORDING) {
            return {
                ...state,
                moduleStatus: ModuleStatus.PAUSED,
                recorderStatus: RecorderStatus.AWAITING_CANCEL_CONFIRMATION,
                previousRecorderStatus: state.recorderStatus,
            };
        } if (state.moduleStatus === ModuleStatus.PAUSED) {
            return {
                ...state,
                recorderStatus: RecorderStatus.AWAITING_CANCEL_CONFIRMATION,
                previousRecorderStatus: state.recorderStatus,
            };
        } if (state.moduleStatus === ModuleStatus.STOPPED) {
            return {
                ...state,
                recorderStatus: RecorderStatus.AWAITING_CANCEL_CONFIRMATION,
                previousRecorderStatus: state.recorderStatus,
            };
        }
        return state;
    case RecordingTypeKeys.DESIST_CANCEL_RECORDING:
        if (state.moduleStatus === ModuleStatus.PAUSED) {
            return {
                ...state,
                moduleStatus: state.previousRecorderStatus === RecorderStatus.RECORDING ? ModuleStatus.RECORDING : ModuleStatus.PAUSED,
                recorderStatus: state.previousRecorderStatus,
                previousRecorderStatus: state.recorderStatus,
            };
        } if (state.moduleStatus === ModuleStatus.STOPPED) {
            return {
                ...state,
                recorderStatus: state.previousRecorderStatus,
                previousRecorderStatus: state.recorderStatus,
            };
        }
        return state;
    case RecordingTypeKeys.CANCEL_RECORDING:
        if (state.moduleStatus === ModuleStatus.PAUSED) {
            return {
                ...state,
                moduleStatus: ModuleStatus.STOPPED,
                recorderStatus: RecorderStatus.RECORDING_CANCELLED,
                previousRecorderStatus: state.recorderStatus,
            };
        }
        return {
            ...initialState,
        };
    case RecordingTypeKeys.CLOSE_RECORDING:
        return {
            ...initialState,
        };
    case RecordingTypeKeys.ASK_REDO_RECORDING:
        if (state.moduleStatus === ModuleStatus.RECORDING) {
            return {
                ...state,
                moduleStatus: ModuleStatus.PAUSED,
                recorderStatus: RecorderStatus.AWAITING_REDO_CONFIRMATION,
                previousRecorderStatus: state.recorderStatus,
            };
        } if (state.moduleStatus === ModuleStatus.PAUSED) {
            return {
                ...state,
                recorderStatus: RecorderStatus.AWAITING_REDO_CONFIRMATION,
                previousRecorderStatus: state.recorderStatus,
            };
        } if (state.moduleStatus === ModuleStatus.STOPPED) {
            return {
                ...state,
                recorderStatus: RecorderStatus.AWAITING_REDO_CONFIRMATION,
                previousRecorderStatus: state.recorderStatus,
            };
        }
        return state;
    case RecordingTypeKeys.DESIST_REDO_RECORDING:
        if (state.moduleStatus === ModuleStatus.PAUSED) {
            return {
                ...state,
                moduleStatus: state.previousRecorderStatus === RecorderStatus.RECORDING ? ModuleStatus.RECORDING : ModuleStatus.PAUSED,
                recorderStatus: state.previousRecorderStatus,
                previousRecorderStatus: state.recorderStatus,
            };
        }
        return state;
    case RecordingTypeKeys.REDO_RECORDING:
        if (state.moduleStatus === ModuleStatus.PAUSED) {
            return {
                ...state,
                moduleStatus: ModuleStatus.STOPPED,
                recorderStatus: RecorderStatus.PENDING_REDO,
                previousRecorderStatus: state.recorderStatus,
            };
        }
        return {
            ...initialState,
        };
    case RecordingTypeKeys.REENACT_RECORDING:
        return {
            ...initialState,
            partyState: {
                ...state.partyState,
            },
            moduleStatus: ModuleStatus.AWAITING_MIC_PERMISSION,
            workspaceId: state.workspaceId,
            questionId: state.questionId,
            featureId: state.featureId,
            assessmentRunId: state.assessmentRunId,
            recordedTime: 0,
            recorderIntervalId: state.recorderIntervalId,
        };
    case RecordingTypeKeys.ASK_FILENAME_FOR_RECORDING:
        return {
            ...state,
            recorderStatus: action.payload.shouldWaitForWorkspace ? RecorderStatus.AWAITING_FILENAME_AND_WORKSPACE : RecorderStatus.AWAITING_FILENAME,
            previousRecorderStatus: state.recorderStatus,
            recordedAudio: {
                ...state.recordedAudio,
                ...action.payload.recordedAudio,
            },
        };
    case RecordingTypeKeys.RETRY_SAVING:
        return {
            ...state,
            recorderStatus: action.payload ? RecorderStatus.AWAITING_FILENAME_AND_WORKSPACE : RecorderStatus.AWAITING_FILENAME,
            previousRecorderStatus: state.recorderStatus,
        };
    case RecordingTypeKeys.SET_FILENAME:
        return {
            ...state,
            recordedAudio: {
                ...state.recordedAudio,
                filename: action.payload,
            },
        };
    default:
        return state;
    }
};

export function getReducer() {
    return explanationRecordingReducer;
}
